import { useEffect, useState } from "react";

import { Check, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { useMutation } from "@redwoodjs/web";

import { IntegrationCard } from "src/components/SettingsDialog/IntegrationCard";
import { ManageModal } from "src/components/SettingsDialog/StoreSettings/ManageModal";
import { Button } from "src/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import { useAuth } from "src/lib/auth";
import { reportingIntegrationTypes } from "src/lib/constants";
import { useIntegration } from "src/lib/hooks/useIntegration";
import { INTEGRATION_URL_MUTATION } from "src/lib/queries/integrations";
import { cn, pluralize } from "src/lib/utils";

const UPDATE_USER_MUTATION = gql`
  mutation UpdateUserMutation($input: UpdateUserInput!) {
    updateUser(input: $input) {
      id
      name
      title
      company
      revenue
    }
  }
`;

const AccountSettings = ({
  manageParam,
  sectionTarget,
  highlightedSection,
  clearManageParam,
}) => {
  const { currentUser, reauthenticate } = useAuth();
  const {
    selectedIntegration,
    loading: loadingIntegration,
    refetch,
  } = useIntegration(reportingIntegrationTypes.slack);

  const [manageModalOpen, setManageModalOpen] = useState(false);

  useEffect(() => {
    if (manageParam === "slack" && selectedIntegration && !manageModalOpen) {
      setManageModalOpen(true);
      clearManageParam?.();
    }
  }, [clearManageParam, manageModalOpen, manageParam, selectedIntegration]);

  const form = useForm({
    defaultValues: {
      name: currentUser?.name || "",
      title: currentUser?.title || "",
      company: currentUser?.company || "",
      revenue: currentUser?.revenue || "",
    },
  });

  const [createIntegrationUrl, { loading: loadingIntegrationUrl }] =
    useMutation(INTEGRATION_URL_MUTATION, {
      onCompleted: (data) => {
        window.open(data.integrationUrl, "_self");
      },
      onError: (error) => {
        toast.error(error.message, {
          description: "Please try again.",
        });
      },
    });
  const [updateUser, { loading: loadingUpdateUser }] = useMutation(
    UPDATE_USER_MUTATION,
    {
      onCompleted: async (data) => {
        form.reset({
          name: data.updateUser.name,
          title: data.updateUser.title,
          company: data.updateUser.company,
          revenue: data.updateUser.revenue,
        });

        // update the current user in the auth context
        await reauthenticate();
      },
    }
  );

  const loading =
    loadingIntegration || loadingUpdateUser || loadingIntegrationUrl;

  const onSubmit = (data) => {
    return toast.promise(
      updateUser({
        variables: {
          id: currentUser.id,
          input: {
            name: data.name,
            title: data.title,
            company: data.company,
            revenue: data.revenue,
          },
        },
      }).then((result) => {
        if (result.errors) {
          throw new Error();
        }
      }),
      {
        loading: "Updating profile...",
        success: "Profile updated successfully!",
        error: (error) => {
          return (
            error.message || "Failed to update profile. Please try again later"
          );
        },
      }
    );
  };

  const handleConnectSlack = () =>
    createIntegrationUrl({
      variables: {
        input: {
          type: reportingIntegrationTypes.slack.id.toUpperCase(),
        },
      },
    });

  const channelsCount = selectedIntegration?.settings?.sources?.length;
  return (
    <>
      <Form {...form}>
        <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter your name" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter your title" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter your company name" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="revenue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Annual Revenue</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter annual revenue" />
                </FormControl>
              </FormItem>
            )}
          />

          <Button type="submit" disabled={loading || !form.formState.isDirty}>
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Check className="size-4" />
                Apply Changes
              </span>
            )}
          </Button>
        </form>
      </Form>

      <div
        data-settings-section="slack"
        className={cn(
          "rounded-xl transition-[background-color,box-shadow] duration-700",
          sectionTarget === "slack" &&
            highlightedSection === "slack" &&
            "bg-primary/5 ring-primary/30 ring-2"
        )}
      >
        <IntegrationCard
          integration={selectedIntegration}
          description={
            channelsCount
              ? `Connected to ${channelsCount} ${pluralize("channel", channelsCount)}`
              : "No reporting channel selected"
          }
          type={reportingIntegrationTypes.slack}
          loading={loading}
          onManage={() => setManageModalOpen(true)}
          onConnect={handleConnectSlack}
          isAdmin
        />
      </div>

      <ManageModal
        integration={selectedIntegration}
        open={manageModalOpen}
        setOpen={() => {
          setManageModalOpen(false);
          clearManageParam?.();
        }}
        multiselect
        descriptionRender={(sources) => {
          if (!sources?.length) {
            return "No channels found. Make sure the app is invited to a channel.";
          }
          return `Found ${sources.length} ${pluralize("channel", sources.length)}. Select the ones you want to use.`;
        }}
        refetch={refetch}
      />
    </>
  );
};

export default AccountSettings;
